import {get} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated with this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'manageVideos';

// Define reducer types handled by this reducer
export const REDUCER_TYPES = {
	RESET: '@manageVideos/reset',
	SET_LIST_DATA: '@manageVideos/set_list_data',
	CLEAR_LIST_DATA: '@manageVideos/clear_list_data',
	SET_ITEM: '@manageVideos/set_item',
	CLEAR_ITEM: '@manageVideos/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} manageVideosListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setManageVideosListData: manageVideosListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: manageVideosListData
	}),
	clearManageVideosListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {ManageVideosItemDataObject} manageVideosItem
	 * @return {{type: string, item: ManageVideosItemDataObject}}
	 */
	setManageVideosItem: manageVideosItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: manageVideosItem
	}),
	clearManageVideosItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export the initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {ManageVideosItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getManageVideosList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getManageVideosListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getManageVideosListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getManageVideosListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getManageVideosItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;